'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

// Short enough to feel instant, long enough for the response to arrive while text is still current.
const DEBOUNCE_MS = 150;

export function useAutocomplete(text: string) {
  const [ghost, setGhost] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestTextRef = useRef(text);

  useEffect(() => {
    latestTextRef.current = text;
    setGhost('');
    abortRef.current?.abort();
    if (timerRef.current) clearTimeout(timerRef.current);

    if (text.trim().length < 3) return;

    timerRef.current = setTimeout(() => {
      const controller = new AbortController();
      abortRef.current = controller;

      fetch('/api/search/autocomplete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
        signal: controller.signal,
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (!data || controller.signal.aborted) return;
          const completion = typeof data.completion === 'string' ? data.completion.trim() : '';
          if (completion && latestTextRef.current === text) {
            setGhost(completion);
          }
        })
        .catch(() => {
          // aborted or network error
        });
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, [text]);

  // Returns the full accepted string and clears ghost
  const accept = useCallback((): string => {
    setGhost('');
    return latestTextRef.current + ghost;
  }, [ghost]);

  const clear = useCallback(() => setGhost(''), []);

  return { ghost, accept, clear };
}
